import type {
    ComputedEventSchedule,
    EventBookingCategory,
    BookingCategory,
    DailyTime,
    Event,
    EventSchedule
} from '@/features/events/types/types';

/**
 * @deprecated This function creates mock EventBookingCategories without proper currency data.
 * Currency should come from the API via EventBookingCategory, not be synthesized here.
 * Use direct API responses instead.
 */
export const convertToEventBookingCategories = (categories: BookingCategory[] | null): EventBookingCategory[] => {
    if (!categories) return [];
    console.warn('convertToEventBookingCategories is deprecated - currency data will be missing. Use API response instead.');
    return categories.map(category => {
        // Keep null as null, don't convert to 0
        const remainingCapacity = category.default_capacity;

        return {
            id: category.id,
            cancelled: false,
            conflict_detected: false,
            capacity: category.default_capacity,
            remaining_capacity: remainingCapacity, // Keep null as null
            price: category.default_price,
            currency: '', // Currency must come from EventBookingCategory API response - empty string makes missing data explicit
            cancellation_fees: category.default_cancellation_fees,
            event_schedule: null,
            booking_category: category,
            custom_slot_duration: category.default_custom_slot_duration
        };
    });
};

export const computeEventSchedules = (eventSchedules: EventSchedule[]): ComputedEventSchedule[] => {
    return eventSchedules.map(schedule => ({
        ...schedule,
        computed_date: new Date(schedule.start_time)
    })).sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
};

// Legacy function - kept for backward compatibility but no longer used
export const generateEventSchedules = (eventInfo: Event): ComputedEventSchedule[] => {
    const events: ComputedEventSchedule[] = [];

    if (eventInfo.event_type === 'SINGLE_DAY') {
        // For single day events, create a schedule for today
        const today = new Date();
        events.push({
            id: `${eventInfo.id}-${today.toISOString()}`,
            start_time: today.toISOString(),
            duration: 7200000000000, // Default 2 hours in nanoseconds
            cancelled: false,
            timezone_id: 'UTC', // Default to UTC for legacy
            event: eventInfo,
            event_booking_categories: convertToEventBookingCategories(eventInfo.booking_categories),
            computed_date: today
        });
    } else if (eventInfo.event_type === 'REPEATED' && eventInfo.repeated_schedule) {
        // For repeated events, generate upcoming dates
        const today = new Date();
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 60); // Show next 60 days

        const dailyTimes = eventInfo.repeated_schedule.daily_times;

        for (let date = new Date(today); date <= endDate; date.setDate(date.getDate() + 1)) {
            const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay(); // Convert Sunday from 0 to 7

            const dailyTime = dailyTimes.find((dt: DailyTime) => dt.day_of_week === dayOfWeek);

            if (dailyTime) {
                const [hours, minutes] = dailyTime.start_time.split(':');
                const eventDateTime = new Date(date);
                eventDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                // Only include future dates
                if (eventDateTime > today) {
                    events.push({
                        id: `${eventInfo.id}-${eventDateTime.toISOString()}`,
                        start_time: eventDateTime.toISOString(),
                        duration: dailyTime.duration,
                        cancelled: false,
                        timezone_id: 'UTC', // Default to UTC for legacy
                        event: eventInfo,
                        event_booking_categories: convertToEventBookingCategories(eventInfo.booking_categories),
                        computed_date: eventDateTime
                    });
                }
            }
        }
    }

    const sortedEvents = events.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    return sortedEvents;
};
